import * as Sentry from "@sentry/react";

// Baked in at build time via Vite's `define` (see vite.config.ts). `typeof`
// guards cover non-Vite contexts (vitest.config.ts has no `define` block) so
// importing this module doesn't ReferenceError before the empty-DSN check.
declare const __SENTRY_DSN__: string;
declare const __SENTRY_ENVIRONMENT__: string;

export function initSentry(): void {
  const dsn = typeof __SENTRY_DSN__ !== "undefined" ? __SENTRY_DSN__ : "";
  const environment =
    typeof __SENTRY_ENVIRONMENT__ !== "undefined" ? __SENTRY_ENVIRONMENT__ : "development";
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment,
    // Route envelopes through our same-origin tunnel — Safari Content
    // Blocker + most adblock lists kill direct `ingest.sentry.io` fetches.
    // Tunnel forwards them server-side from /apps/web/functions/sentry-tunnel.js.
    tunnel: "/sentry-tunnel",
    // Single-user app — sample everything. Free-tier 10K spans/mo is plenty
    // at current traffic; revisit if it ever overflows.
    tracesSampleRate: 1.0,
    // Profiling on the browser samples JS execution for slow renders / long
    // tasks. Same 100% rate as tracing; piggybacks on traced transactions.
    profilesSampleRate: 1.0,
    // Without these the SDK won't attach `sentry-trace` + `baggage` to fetch
    // calls. Same-origin (Pages Function proxy) covers prod; localhost covers
    // local Vite + wrangler dev.
    tracePropagationTargets: [/^\//, "localhost"],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
      // Captures console.log/warn/error as Sentry Logs (joined to traces
      // by trace_id). Matches the worker-side pattern from PR #72.
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    enableLogs: true,
    // 0% background replay; 100% on errors. Sentry's free tier caps replays
    // at 50/mo, so background sampling is too expensive. Every captured
    // error gets the leading ~60s of session attached for debugging — by
    // far the highest-signal use of the budget.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
  });
}

export { Sentry };
